import { EditableFormInstance } from "@ant-design/pro-components";
import { Form, Spin, Tabs, message } from "antd";
import { ROUTES } from "base/routes/routes";
import BoxContainer from "base/ui/components/BoxContainer";
import dayjs from "dayjs";
import { observer } from "mobx-react";
import { DeliveryInfoItem, WasteHandoverRegistrationDetailModel } from "models/WasteHandoverRegistration";
import PageWrapper from "pages/Components/PageWrapper";
import { SearchForm } from "pages/Components/SearchBox";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { IWasteHandoverPlanServices, WasteHandoverPlanModel } from "services/Main/wasteHandoverPlan_services";
import { commonMasterDataPresenter, commonMasterDataStore, commonPresenter, commonStore, wasteHandoverPlanPresenter } from "stores/root_store";
import { SYSTEM_TITLE } from "utils/constants";
import { getRole, getRoles } from "utils/localStorageHelper";
import { checkPermissionWithId } from "utils/permissionHelper";
import CustomButton from "base/ui/components/Button";
import { Trash } from "base/ui/components/Icons";
import CancelConfirm from "../CancelConfirm";
import ActionButtons from "./ActionButtons";
import { CardBox } from './cardBox';
import DriverInformation from "./DriverInformation";
import GeneralInformation from "./GeneralInformation";
import HandoverInfoCreate from "./HandoverInfoCreate";
import HandoverInfoEdit from "./HandoverInfoEdit";
import { TabHistory } from './TabHistory';
import WasteHandoverRegistrationTimeline, { WasteHandoverRegistrationTimelineRef } from "./WasteHandoverRegistrationTimeline";
import WasteHandoverRegistrationTransitionStatus, { WasteHandoverRegistrationTransitionStatusRef } from "./WasteHandoverRegistrationTransitionStatus";

// Interface mở rộng cho wasteOptions với contract info
export interface WasteOptionWithContract {
  label: string;
  value: string;
  productName?: string;
  wasteGroupingContractId?: string;
}

export function createCreateUpdateWasteHandoverRegistration(wasteHandoverPlanServices: IWasteHandoverPlanServices) {
  return observer(() => {
    const { t } = useTranslation();
    document.title = t('waste-handover-plan-register.create-title') + ' - ' + SYSTEM_TITLE;

    const role: string = getRole()
    // Lấy roles từ localStorage
    const rolesRaw = getRoles();
    const roles = Array.isArray(rolesRaw)
      ? rolesRaw
      : (typeof rolesRaw === 'string' ? rolesRaw.split(',').map(r => r.trim()) : []);
    const isHuyPhieuChatThai = roles.includes('HUYPHIEUCHATTHAI');
    const canCancel = checkPermissionWithId('CANCEL') || isHuyPhieuChatThai;
    const [modalCancelOpen, setModalCancelOpen] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);

    // lấy id từ url - có thể là wasteHandoverPlanId hoặc wasteHandoverRegistrationId
    const { id: wasteHandoverPlanId, wasteHandoverRegistrationId } = useParams<{ id?: string; wasteHandoverRegistrationId?: string }>();

    const [formCreate] = Form.useForm()
    const editableFormRef = useRef<EditableFormInstance>();
    // lấy data từ màn danh sách truyền qua
    const location = useLocation()
    const navigate = useNavigate()
    // data từ màn danh sách truyền qua
    const dataFromList: WasteHandoverPlanModel[] | undefined = location.state

    const [pageTitle, setPageTitle] = useState(
      (wasteHandoverPlanId || wasteHandoverRegistrationId)
        ? t('waste-handover-plan-register.update-title')
        : t('waste-handover-plan-register.create-title')
    )
    // nếu không có data truyền qua thì quay lại trang danh sách
    if (!dataFromList && !wasteHandoverPlanId && !wasteHandoverRegistrationId) {
      if (wasteHandoverRegistrationId) {
        window.location.assign(ROUTES.WASTE_HANDOVER_REGISTRATION.LIST_REGISTRATION.LINK)
      } else {
        window.location.assign(ROUTES.WASTE_HANDOVER_PLAN.LINK)
      }
      return <Spin spinning ><div className="sticky w-screen h-screen"></div></Spin>
    }
    // loading trang
    const [loading, setLoading] = useState(false)
    // form của table
    const [editTableForm] = Form.useForm()
    // chi tiết phiếu
    const [dataDetail, setDataDetail] = useState<WasteHandoverPlanModel | undefined>(undefined)
    const [dataTable, setDataTable] = useState<WasteHandoverPlanModel[]>([])
    // State để quản lý data khi tạo mới (có thể xóa dòng)
    const [dataForCreate, setDataForCreate] = useState<WasteHandoverPlanModel[]>([])
    // chi tiết phiếu đăng ký (khi edit registration)
    const [dataDetailRegistration, setDataDetailRegistration] = useState<WasteHandoverRegistrationDetailModel | undefined>(undefined)
    // cho phép chỉnh sửa hay không
    const [accessUpdated, setAccessUpdated] = useState(true)

    // Options cho dropdowns
    const [wasteOptions, setWasteOptions] = useState<WasteOptionWithContract[]>([]);
    const [contractOptionsMap, setContractOptionsMap] = useState<Map<string, string>>(new Map());
    const [vendorOptions, setVendorOptions] = useState<{ label: string; value: string }[]>([]);
    const [storageOptions, setStorageOptions] = useState<{ label: string; value: string }[]>([]);
    const [wasteGroupOptions, setWasteGroupOptions] = useState<{ label: string; value: string }[]>([]);

    // State cho driver information
    const [isCheckedPolicyConfirm, setIsCheckedPolicyConfirm] = useState(false);

    // timeline ref
    const transitionStatusRef = useRef<WasteHandoverRegistrationTransitionStatusRef>(null)
    const timelineRef = useRef<WasteHandoverRegistrationTimelineRef>(null)

    // Hàm callback để nhận dữ liệu từ DriverInformation
    const handlePolicyConfirmChange = useCallback((checked: boolean) => {
      setIsCheckedPolicyConfirm(checked);
    }, []);

    // Load dropdown options
    const loadWasteOptions = async () => {
      try {
        const result = await commonPresenter.getDropdownProductWaste();
        if (result && result.length > 0) {
          setWasteOptions(
            result.map((item: any) => {
              // Tách value "145000045 | Can nhựa 17L rỗng" thành mã và tên
              const valueStr = item.value || '';
              const parts = valueStr.split(' | ');
              const productCode = parts[0] || item.key || '';
              const productName = parts.length > 1 ? parts.slice(1).join(' | ') : valueStr;

              return {
                label: valueStr, // Giữ nguyên để hiển thị trong dropdown
                value: item.key || productCode,
                productName: productName, // Tên riêng để dùng khi update
                wasteGroupingContractId: item.wasteGroupingContractId, // Lưu contract ID để map sau
              };
            })
          );
        }
      } catch (error) {
        console.error('Error loading waste options:', error);
      }
    };

    // Load contract options và tạo map
    const loadContractOptions = async () => {
      try {
        await commonMasterDataPresenter.getDropdownWasteGroupingContract(commonMasterDataStore, true);
        if (commonMasterDataStore.wasteGroupingContractOptions && commonMasterDataStore.wasteGroupingContractOptions.length > 0) {
          // Tạo map từ key (contractId) sang value (tên contract)
          const map = new Map<string, string>();
          commonMasterDataStore.wasteGroupingContractOptions.forEach((item: any) => {
            if (item.key && item.value) {
              // Tách tên contract từ value "1000004 | Phi kim"
              const parts = item.value.split(' | ');
              const contractName = parts.length > 1 ? parts.slice(1).join(' | ') : item.value;
              map.set(item.key, contractName);
            }
          });
          setContractOptionsMap(map);
        }
      } catch (error) {
        console.error('Error loading contract options:', error);
      }
    };

    const loadVendorOptions = async () => {
      try {
        await commonMasterDataPresenter.getCommonDataByKey(commonMasterDataStore, 'VendorType');
        const vendorTypes = commonMasterDataStore.commonRespone?.data || [];
        const wasteType = vendorTypes.find((item) => item.value?.toUpperCase() === 'NCC_CT');
        const type = wasteType?.key || 'NCC_CT';

        await commonPresenter.getDropdownVendor(commonStore, { type });
        if (commonStore.commonRespone.isSuccess) {
          setVendorOptions(
            commonStore.commonRespone.data.map((item) => ({
              label: item.value || '',
              value: item.key || '',
            }))
          );
        }
      } catch (error) {
        console.error('Error loading vendor options:', error);
      }
    };

    const loadStorageOptions = async () => {
      try {
        await commonMasterDataPresenter.getCommonDataByKey(commonMasterDataStore, 'StockType');
        const stockTypes = commonMasterDataStore.commonRespone?.data || [];
        const wasteType = stockTypes.find((item) => item.value?.toUpperCase() === 'KCT');
        const type = wasteType?.key || 'KCT';

        await commonPresenter.getDropdownStock(commonStore, { type });
        if (commonStore.commonRespone.isSuccess) {
          setStorageOptions(
            commonStore.commonRespone.data.map((item) => ({
              label: item.value || '',
              value: item.key || '',
            }))
          );
        }
      } catch (error) {
        console.error('Error loading storage options:', error);
      }
    };

    const loadWasteGroupOptions = async () => {
      try {
        if (!commonMasterDataStore.wasteGroupingEnvironmentPermitOptions.length) {
          await commonMasterDataPresenter.getDropdownWasteGroupingEnvironmentPermit(commonMasterDataStore);
        }
        setWasteGroupOptions(
          (commonMasterDataStore.wasteGroupingEnvironmentPermitOptions || []).map((item) => ({
            label: item.value,
            value: item.key,
          }))
        );
      } catch (error) {
        console.error('Error loading waste group options:', error);
      }
    };

    // Kiểm tra xem có đang ở chế độ edit không
    const isEditMode = !!(wasteHandoverPlanId || wasteHandoverRegistrationId);

    // Đã chuyển logic columns sang HandoverInfoCreate và HandoverInfoEdit components


    // hàm lưu thêm mới
    const handleSave = async (values: any) => {
      const driverName = (formCreate.getFieldValue('driverName') || '').toString().trim();
      const phone = (formCreate.getFieldValue('phone') || '').toString().trim();
      const licensePlate = (formCreate.getFieldValue('licensePlate') || '').toString().trim();
      const licenseNumber = (formCreate.getFieldValue('licenseNumber') || '').toString().trim();
      const companionInfo = (formCreate.getFieldValue('companionInfo') || '').toString().trim();
      const hasDriverInfo = !!(driverName || phone || licensePlate || licenseNumber || companionInfo);

      if (hasDriverInfo && !isCheckedPolicyConfirm) {
        message.error(t('waste-handover-plan-register.driver-consent-required'));
        return;
      }

      // dữ liệu nhập trong bảng
      const dataEditTable: any = editTableForm.getFieldsValue()

      editTableForm.validateFields().catch(() => { })

      if (Object.keys(dataEditTable).length === 0) {
        message.error(t('waste-handover-plan-register.goods-info-required'));
        return;
      }

      const deliveryTimeItems = formCreate.getFieldValue('deliveryTimeItems') || [];
      if (!deliveryTimeItems || deliveryTimeItems.length === 0) {
        message.error(t('waste-handover-plan.delivery-schedule-time-required'));
        return;
      }

      // Lấy vendorNumber từ form hoặc dataFromList/dataForCreate
      const sourceData = dataForCreate.length > 0 ? dataForCreate : (dataFromList || []);
      const vendorNumber = formCreate.getFieldValue('vendorNumber') || sourceData?.[0]?.vendorNumber || '';

      // Helper function để convert string rỗng thành null cho GUID fields
      const toGuidOrNull = (value: any): string | null => {
        if (!value || value === '') return null;
        return value;
      };

      // Map deliveryInfoItems từ dataEditTable và dataFromList/dataForCreate
      const sourceDataForSave = dataForCreate.length > 0 ? dataForCreate : (dataFromList || []);

      // Helper function để check xem item có phải là dòng mới không (không có wasteHandoverPlanId hợp lệ)
      const isNewRow = (item: any, originalItem: any): boolean => {
        const planId = originalItem?.wasteHandoverPlanId || item?.wasteHandoverPlanId || '';
        const planCode = originalItem?.wasteHandoverPlanCode || item?.wasteHandoverPlanCode || '';
        // Dòng mới là dòng không có wasteHandoverPlanId hợp lệ (rỗng hoặc GUID rỗng) hoặc không có wasteHandoverPlanCode
        return !planId || planId === '' || planId === '00000000-0000-0000-0000-000000000000' || !planCode;
      };

      // Check validation trước khi map
      // Trong handleSave, dataEditTable là object với key là wasteHandoverPlanId
      // Cần check các dòng mới (không có trong sourceDataForSave hoặc không có wasteHandoverPlanId hợp lệ)
      const dataEditTableEntries = Object.entries(dataEditTable);
      const newRowsWithoutProduct = dataEditTableEntries.filter(([key, item]: [string, any]) => {
        // Tìm originalItem từ sourceDataForSave dựa trên wasteHandoverPlanId
        const originalItem = sourceDataForSave.find((row: any) => row.wasteHandoverPlanId === key) || item;
        const isNew = isNewRow(item, originalItem);
        const productCode = item.productCode !== undefined && item.productCode !== null
          ? item.productCode
          : (originalItem?.productCode || '');
        return isNew && (!productCode || productCode.trim() === '');
      });

      if (newRowsWithoutProduct.length > 0) {
        message.error(t('waste-handover-plan-register.please-select-waste-scrap'));
        return;
      }

      const deliveryInfoItems = dataEditTableEntries.map(([key, item]: [string, any], index: number) => {
        const originalItem = sourceDataForSave.find((row: any) => row.wasteHandoverPlanId === key) || item;
        return {
          stt: item.stt !== undefined && item.stt !== null ? item.stt : (originalItem.stt || index + 1),
          wasteHandoverPlanId: originalItem.wasteHandoverPlanId || '',
          wasteHandoverPlanCode: originalItem.wasteHandoverPlanCode || '',
          productCode: item.productCode !== undefined && item.productCode !== null ? item.productCode : (originalItem.productCode || ''),
          productName: item.materialName || item.productName || originalItem.materialName || originalItem.productName || '',
          plannedQuantity: (originalItem as any).plannedQuantity || (originalItem as any).actualHandoverQuantity || 0,
          deliveredQuantity: item.actualHandoverQuantity || 0,
          openQuantity: (originalItem as any).openQuantity || 0,
          storageLocation: item.storageLocation || originalItem.storageLocation || '',
          storageLocationName: item.storageLocationName || originalItem.storageLocationName || '',
          deliveryStatus: item.deliveryStatus !== undefined ? item.deliveryStatus : ((originalItem as any).deliveryStatus || ''),
          deliveryStatusName: item.deliveryStatusName || (originalItem as any).deliveryStatusName || '',
          wasteGroupingContractId: toGuidOrNull(item.wasteGroupingContractId || (originalItem as any).wasteGroupingContractId),
          wasteGroupingContractName: originalItem.wasteGroupingContractName || item.wasteGroupingContractName || '',

          firstWeight: item.firstWeight !== undefined && item.firstWeight !== null ? item.firstWeight : ((originalItem as any).firstWeight || null),
          secondWeight: item.secondWeight !== undefined && item.secondWeight !== null ? item.secondWeight : ((originalItem as any).secondWeight || null),
          packingWeight: item.packingWeight !== undefined && item.packingWeight !== null ? item.packingWeight : ((originalItem as any).packingWeight || null),

          actualDeliveryWeight: item.actualDeliveryWeight !== undefined && item.actualDeliveryWeight !== null ? item.actualDeliveryWeight : ((originalItem as any).actualDeliveryWeight || null),
          unit: item.unit || (originalItem as any).unit || 'KG',
          note: item.note !== undefined ? item.note : (originalItem.note || ''),
        };
      })
        .filter((item: any) => {
          // Chỉ giữ lại các item có productCode hợp lệ (không rỗng)
          return item?.productCode && item.productCode.trim() !== '';
        });

      // Tính tổng deliveredQuantity từ deliveryInfoItems
      const totalDeliveredQuantity = deliveryInfoItems.reduce((sum, item) => sum + (item.deliveredQuantity || 0), 0);

      // Map deliveryTimeItems - nếu chỉ có 1 item thì dùng tổng deliveredQuantity
      const mappedDeliveryTimeItems = deliveryTimeItems.map((item: any, index: number) => {
        // Format deliveryTime thành local time string (không kèm timezone) để tránh bị trừ 7 giờ
        let formattedDeliveryTime = '';
        if (item.deliveryTime) {
          // Nếu deliveryTime đã là string format YYYY-MM-DDTHH:mm:00 hoặc YYYY-MM-DDTHH:mm:ss, chỉ cần thêm .000 cho milliseconds
          if (typeof item.deliveryTime === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(item.deliveryTime)) {
            formattedDeliveryTime = item.deliveryTime + '.000';
          } else if (typeof item.deliveryTime === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}$/.test(item.deliveryTime)) {
            // Nếu đã có milliseconds, dùng trực tiếp
            formattedDeliveryTime = item.deliveryTime;
          } else {
            // Nếu là dayjs object hoặc string khác, parse và format
            const deliveryTimeDayjs = dayjs(item.deliveryTime);
            formattedDeliveryTime = deliveryTimeDayjs.isValid()
              ? deliveryTimeDayjs.format('YYYY-MM-DDTHH:mm:ss.SSS')
              : dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS');
          }
        } else {
          formattedDeliveryTime = dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS');
        }

        return {
          deliveryTime: formattedDeliveryTime,
          deliveryQuantity: deliveryTimeItems.length === 1 ? totalDeliveredQuantity : (item.deliveryQuantity || 0),
          wasteHandoverRegistrationTimeId: item.wasteHandoverRegistrationTimeId,
          isDelivered: item.isDelivered || false,
        };
      });

      // Lấy thông tin tài xế (driverName, phone, licensePlate, licenseNumber, companionInfo đã khai báo ở đầu handleSave)
      const result = formCreate.getFieldValue('result') || '';

      // Xác định periodTime và isTime
      const periodTime = deliveryTimeItems[0]?.periodTime || 'SANG';
      const isTime = !!deliveryTimeItems[0]?.time; // Có nhập giờ hay không

      const request = {
        vendorNumber: sourceDataForSave?.[0]?.vendorNumber || vendorNumber,
        deliveryTimeItems: mappedDeliveryTimeItems,
        driverName,
        phone,
        licensePlate,
        licenseNumber,
        result,
        companionInfo,
        deliveryInfoItems: deliveryInfoItems,
        isSendEmail: formCreate.getFieldValue('isSendEmail') || false,
        periodTime: periodTime,
        isTickCheckBox: isCheckedPolicyConfirm,
        isTime: isTime,
      };

      setLoading(true)
      try {
        const result = await wasteHandoverPlanPresenter.createWasteHandoverRegistration(request)
        setLoading(false)
        if (result.isSuccess) {
          message.success(t('waste-handover-plan-register.create-success'));
          // navigate đến chính nó (vì trang này làm chung cho create và update, nhưng khác là có thêm /id)
          navigate(`${ROUTES.WASTE_HANDOVER_REGISTRATION.EDIT_REGISTRATION.LINK}/${result?.data}`)
        } else {
          message.error(result.message || t('message.error'));
        }
      } catch (err) {
        setLoading(false)
        message.error(t('waste-handover-plan-register.create-error'));
      }
    }


    // hàm cập nhật
    const handleUpdate = async (values: any, option?: {
      isHideMessage?: boolean;
      isContinue?: boolean;
      tareData?: {
        noPacking: number;
        perPackingWeight: number;
        packingWeight: number;
      };
      confirmedDetailIds?: string[]; // Danh sách các item được chọn "XN bàn giao" (DBG)
      deliveryStatusesMap?: Record<string, string>; // Map deliveryStatuses để cập nhật deliveryStatus
      outboundWeighingData?: {
        secondWeight: number;
        deliveredQuantity: number;
      }; // Dữ liệu cân ra
    }) => {
      const driverName = (formCreate.getFieldValue('driverName') || '').toString().trim();
      const phone = (formCreate.getFieldValue('phone') || '').toString().trim();
      const licensePlate = (formCreate.getFieldValue('licensePlate') || '').toString().trim();
      const licenseNumber = (formCreate.getFieldValue('licenseNumber') || '').toString().trim();
      const companionInfo = (formCreate.getFieldValue('companionInfo') || '').toString().trim();
      const hasDriverInfo = !!(driverName || phone || licensePlate || licenseNumber || companionInfo);

      if (hasDriverInfo && !isCheckedPolicyConfirm) {
        message.error(t('waste-handover-plan-register.driver-consent-required'));
        return Promise.resolve(false);
      }

      // Nếu là update wasteHandoverRegistration
      if (wasteHandoverRegistrationId) {
        // dữ liệu nhập trong bảng
        let dataEditTable: any = editTableForm.getFieldsValue()

        editTableForm.validateFields().catch(() => { })

        if (Object.keys(dataEditTable).length === 0) {
          message.error(t('waste-handover-plan-register.goods-info-required'));
          return Promise.resolve(false);
        }

        dataTable.forEach((row: any) => {
          const detailId = (row as any).wasteHandoverRegistrationDetailId;
          if (!detailId) return;

          if (!dataEditTable[detailId]) {
            dataEditTable[detailId] = { ...row, wasteHandoverRegistrationDetailId: detailId };
          } else {
            if ((!dataEditTable[detailId].productCode || dataEditTable[detailId].productCode === '') && row.productCode) {
              dataEditTable[detailId].productCode = row.productCode;
            }
            if ((!dataEditTable[detailId].materialName && !dataEditTable[detailId].productName) && (row.materialName || row.productName)) {
              dataEditTable[detailId].materialName = row.materialName || row.productName;
              dataEditTable[detailId].productName = row.materialName || row.productName;
            }
          }
        });

        const deliveryTimeItems = formCreate.getFieldValue('deliveryTimeItems') || [];
        const deliveryTime = deliveryTimeItems[0]?.deliveryTime || dayjs().toISOString();

        const toGuidOrNull = (value: any): string | null => {
          if (!value || value === '') return null;
          return value;
        };

        // Helper function để check xem item có phải là dòng mới không (không có wasteHandoverPlanId hợp lệ)
        const isNewRow = (item: any, originalItem: any): boolean => {
          const planId = item?.wasteHandoverPlanId || originalItem?.wasteHandoverPlanId || '';
          const planCode = item?.wasteHandoverPlanCode || originalItem?.wasteHandoverPlanCode || '';
          // Dòng mới là dòng không có wasteHandoverPlanId hợp lệ (rỗng hoặc GUID rỗng) hoặc không có wasteHandoverPlanCode
          return !planId || planId === '' || planId === '00000000-0000-0000-0000-000000000000' || !planCode;
        };

        // Check validation trước khi map
        const dataEditTableEntries = Object.entries(dataEditTable);
        const newRowsWithoutProduct = dataEditTableEntries.filter(([recordKey, item]: [string, any]) => {
          let detailId = item?.wasteHandoverRegistrationDetailId;
          if (!detailId) {
            const foundItem = dataTable.find((row: any) =>
              (row as any).wasteHandoverRegistrationDetailId === recordKey ||
              row.wasteHandoverPlanId === recordKey
            ) as any;
            detailId = foundItem?.wasteHandoverRegistrationDetailId;
          }
          if (!detailId) return false;

          const originalItem = dataTable.find((row: any) =>
            (row as any).wasteHandoverRegistrationDetailId === detailId
          ) as any;

          const isNew = isNewRow(item, originalItem);
          const productCode = item?.productCode || originalItem?.productCode || '';
          return isNew && (!productCode || productCode.trim() === '');
        });

        if (newRowsWithoutProduct.length > 0) {
          message.error(t('waste-handover-plan-register.please-select-waste-scrap'));
          return Promise.resolve(false);
        }

        const mappedItems = dataEditTableEntries
          .map(([recordKey, item]: [string, any]) => {
            let detailId = item?.wasteHandoverRegistrationDetailId;
            if (!detailId) {
              const foundItem = dataTable.find((row: any) =>
                (row as any).wasteHandoverRegistrationDetailId === recordKey ||
                row.wasteHandoverPlanId === recordKey
              ) as any;
              detailId = foundItem?.wasteHandoverRegistrationDetailId;
            }
            if (!detailId) return null;

            const originalItem = dataTable.find((row: any) =>
              (row as any).wasteHandoverRegistrationDetailId === detailId
            ) as any;

            const wasteHandoverPlanCode = item.wasteHandoverPlanCode || originalItem?.wasteHandoverPlanCode || '';
            const isNewRowNotFromPlan = !wasteHandoverPlanCode && !detailId;

            const productCode = item?.productCode || originalItem?.productCode || '';
            const productName = item?.materialName || item?.productName || originalItem?.materialName || originalItem?.productName || '';

            return {
              wasteHandoverRegistrationDetailId: detailId,
              stt: item.stt ?? originalItem?.stt ?? 0,
              wasteHandoverPlanId: isNewRowNotFromPlan
                ? '00000000-0000-0000-0000-000000000000'
                : (item.wasteHandoverPlanId || originalItem?.wasteHandoverPlanId || '00000000-0000-0000-0000-000000000000'),
              wasteHandoverPlanCode: wasteHandoverPlanCode,
              productCode: productCode,
              productName: productName,
              plannedQuantity: originalItem?.plannedQuantity || originalItem?.actualHandoverQuantity || 0,
              deliveredQuantity: item.actualHandoverQuantity ?? originalItem?.actualHandoverQuantity ?? 0,
              openQuantity: originalItem?.openQuantity || 0,
              storageLocation: item.storageLocation || originalItem?.storageLocation || '',
              storageLocationName: item.storageLocationName || originalItem?.storageLocationName || '',
              deliveryStatus: option?.deliveryStatusesMap?.[detailId] || item.deliveryStatus || originalItem?.deliveryStatus || '',
              deliveryStatusName: item.deliveryStatusName || originalItem?.deliveryStatusName || '',
              wasteGroupingContractId: toGuidOrNull(item.wasteGroupingContractId || originalItem?.wasteGroupingContractId),
              wasteGroupingContractName: originalItem?.wasteGroupingContractName || item.wasteGroupingContractName || '',
              firstWeight: item.firstWeight ?? originalItem?.firstWeight ?? null,
              secondWeight: (option?.outboundWeighingData && originalItem?.isChoCanRa === true)
                ? option.outboundWeighingData.secondWeight
                : (item.secondWeight ?? originalItem?.secondWeight ?? null),


              actualDeliveryWeight: (option?.outboundWeighingData && originalItem?.isChoCanRa === true)
                ? option.outboundWeighingData.deliveredQuantity
                : (item.actualDeliveryWeight ?? originalItem?.deliveredQuantity ?? null),
              unit: item.unit || originalItem?.unit || 'KG',
              note: item.note ?? originalItem?.note ?? '',
              noPacking: (option?.tareData && option?.confirmedDetailIds?.includes(detailId))
                ? option.tareData.noPacking
                : (originalItem?.noPacking || 0),
              perPackingWeight: (option?.tareData && option?.confirmedDetailIds?.includes(detailId))
                ? option.tareData.perPackingWeight
                : (originalItem?.perPackingWeight || 0),
              packingWeight: (option?.tareData && option?.confirmedDetailIds?.includes(detailId))
                ? option.tareData.packingWeight
                : (originalItem?.packingWeight || 0),
            };
          })
          .filter((item: any): item is any => item !== null);

        const seenDetailIds = new Set<string>();
        const deliveryInfoItems = mappedItems.filter((item: any) => {
          const detailId = item.wasteHandoverRegistrationDetailId;
          if (!detailId || seenDetailIds.has(detailId)) return false;
          seenDetailIds.add(detailId);
          return true;
        });

        // Map deliveryTimeItems
        const mappedDeliveryTimeItems = deliveryTimeItems.map((item: any) => {
          // Format deliveryTime thành local time string (không kèm timezone) để tránh bị trừ 7 giờ
          let formattedDeliveryTime = '';
          if (item.deliveryTime) {
            // Nếu deliveryTime đã là string format YYYY-MM-DDTHH:mm:00 hoặc YYYY-MM-DDTHH:mm:ss, chỉ cần thêm .000 cho milliseconds
            if (typeof item.deliveryTime === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(item.deliveryTime)) {
              formattedDeliveryTime = item.deliveryTime + '.000';
            } else if (typeof item.deliveryTime === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}$/.test(item.deliveryTime)) {
              // Nếu đã có milliseconds, dùng trực tiếp
              formattedDeliveryTime = item.deliveryTime;
            } else {
              // Nếu là dayjs object hoặc string khác, parse và format
              const deliveryTimeDayjs = dayjs(item.deliveryTime);
              formattedDeliveryTime = deliveryTimeDayjs.isValid()
                ? deliveryTimeDayjs.format('YYYY-MM-DDTHH:mm:ss.SSS')
                : dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS');
            }
          } else {
            formattedDeliveryTime = dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS');
          }

          return {
            deliveryTime: formattedDeliveryTime,
            deliveryQuantity: item.deliveryQuantity || 0,
            wasteHandoverRegistrationTimeId: item.wasteHandoverRegistrationTimeId,
            isDelivered: item.isDelivered || false,
          };
        });

        // Lấy thông tin tài xế
        const driverName = formCreate.getFieldValue('driverName') || '';
        const phoneValue = formCreate.getFieldValue('phone') || '';
        const licensePlate = formCreate.getFieldValue('licensePlate') || '';
        const licenseNumberValue = formCreate.getFieldValue('licenseNumber') || '';
        const result = formCreate.getFieldValue('result') || '';
        const companionInfo = formCreate.getFieldValue('companionInfo') || '';

        // Xác định periodTime và isTime
        const periodTime = deliveryTimeItems[0]?.periodTime || 'SANG';
        const isTime = !!deliveryTimeItems[0]?.time;

        const request = {
          wasteHandoverRegistrationId: wasteHandoverRegistrationId,
          vendorNumber: dataDetailRegistration?.vendorNumber || '',
          deliveryTimeItems: mappedDeliveryTimeItems,
          driverName: driverName,
          phone: phoneValue,
          licensePlate: licensePlate,
          licenseNumber: licenseNumberValue,
          result: result,
          companionInfo: companionInfo,
          isSendEmail: formCreate.getFieldValue('isSendEmail') || false,
          deliveryInfoItems: deliveryInfoItems,
          periodTime: periodTime,
          isTickCheckBox: isCheckedPolicyConfirm,
          isTime: isTime,
        };

        setLoading(true)
        try {
          const result = await wasteHandoverPlanPresenter.updateWasteHandoverRegistration(request)
          setLoading(false)
          if (result.isSuccess) {
            if (!option?.isHideMessage) {
              message.success(t('waste-handover-plan-register.update-success'));
            }
            if (option?.isContinue) {
              await getDetailWasteHandoverRegistration()
              timelineRef.current?.reload();
            } else {
              // navigate(ROUTES.WASTE_HANDOVER_REGISTRATION.LIST_REGISTRATION.LINK)
              // reload lại trang
              // Reload detail registration data
              await getDetailWasteHandoverRegistration();
              // Reload status timeline
              transitionStatusRef.current?.reload();
              // Reload timeline
              timelineRef.current?.reload();
            }
            return Promise.resolve(true)
          } else {
            message.error(result.message || t('message.error'));
            return Promise.resolve(false)
          }
        } catch (err) {
          setLoading(false)
          message.error(t('waste-handover-plan-register.update-error'));
          return Promise.resolve(false)
        }
      }

      // Nếu là update wasteHandoverPlan (logic cũ)
      // dữ liệu nhập trong bảng
      const dataEditTable: any = editTableForm.getFieldsValue()

      editTableForm.validateFields().catch(() => { })

      if (Object.keys(dataEditTable).length === 0) {
        message.error(t('waste-handover-plan-register.goods-info-required'));
        return Promise.resolve(false);
      }

      const item = Object.values(dataEditTable)[0] as any;

      const deliveryTimeItems = formCreate.getFieldValue('deliveryTimeItems') || [];
      const deliveryTime = deliveryTimeItems[0]?.deliveryTime || dayjs().toISOString();

      const request = {
        wasteHandoverPlanId: wasteHandoverPlanId || '',
        deliveryDate: deliveryTime,
        productCode: item.productCode || '',
        productName: item.productName || '',
        vendorNumber: item.vendorNumber || '',
        vendorName: item.vendorName || '',
        storageLocation: item.storageLocation || '',
        handoverDocumentNumber: item.handoverDocumentNumber || '',
        actualHandoverQuantity: item.actualHandoverQuantity || 0,
        note: item.note || '',
        actived: item.actived !== undefined ? item.actived : true,
        isSendEmail: formCreate.getFieldValue('isSendEmail') || false,
        isTickCheckBox: isCheckedPolicyConfirm,
      };

      setLoading(true)
      try {
        const result = await wasteHandoverPlanPresenter.updateWasteHandoverPlan(request)
        setLoading(false)
        if (result.isSuccess) {
          if (!option?.isHideMessage) {
            message.success(t('waste-handover-plan-register.update-success'));
          }
          if (option?.isContinue) {
            await getDetailWasteHandoverPlan()
          } else {
            // navigate(ROUTES.WASTE_HANDOVER_PLAN.LINK)
            // reload lại trang
            // Reload detail registration data
            await getDetailWasteHandoverRegistration();
            // Reload status timeline
            transitionStatusRef.current?.reload();
            // Reload timeline
            timelineRef.current?.reload();
          }
          return Promise.resolve(true)
        } else {
          message.error(result.message || t('message.error'));
          return Promise.resolve(false)
        }
      } catch (err) {
        setLoading(false)
        message.error(t('waste-handover-plan-register.update-error'));
        return Promise.resolve(false)
      }
    }

    // chi tiết kế hoạch theo id
    const getDetailWasteHandoverPlan = async () => {
      if (!wasteHandoverPlanId) return;
      setLoading(true)
      const detail = await wasteHandoverPlanPresenter.getWasteHandoverPlanDetail(wasteHandoverPlanId)
      setLoading(false)
      if (detail && detail.isSuccess && detail.data) {
        const data = detail.data;
        setDataDetail(data)
        setDataTable([data])

        // Set form values
        const dataFormEdit = {
          [data.wasteHandoverPlanId]: {
            ...data,
            deliveryDate: data.deliveryDate ? dayjs(data.deliveryDate) : null,
          }
        };
        editTableForm.setFieldsValue(dataFormEdit)

        // Set form create values
        const deliveryTimeItems = data.deliveryDate ? [{
          deliveryTime: data.deliveryDate,
          day: dayjs(data.deliveryDate),
          time: dayjs(data.deliveryDate),
          periodTime: (data as any).periodTime || 'SANG',
        }] : [];

        formCreate.setFieldsValue({
          vendorNumber: data.vendorNumber,
          vendorName: data.vendorName,
          deliveryTimeItems: deliveryTimeItems,
          isSendEmail: (data as any).isSendEmail || false,
        })

        // Set driver information if exists
        if ((data as any).driverName) {
          formCreate.setFieldsValue({
            driverName: (data as any).driverName,
            phone: (data as any).phone,
            licensePlate: (data as any).licensePlate,
            licenseNumber: (data as any).licenseNumber,
            result: (data as any).result || 'DAT',
            companionInfo: (data as any).companionInfo || '',
          })
        }

        setIsCheckedPolicyConfirm((data as any).isTickCheckBox || false)

        setPageTitle(`${t('waste-handover-plan-register.update-title')} - ${data.handoverDocumentNumber || ''}`)
        setAccessUpdated(true)
      }
    }

    // chi tiết phiếu đăng ký theo id
    const getDetailWasteHandoverRegistration = async () => {
      if (!wasteHandoverRegistrationId) return;
      setLoading(true)
      try {
        const detail = await wasteHandoverPlanPresenter.getDetailWasteHandoverRegistration(wasteHandoverRegistrationId)
        setLoading(false)
        if (detail) {
          setDataDetailRegistration(detail)
          // Map deliveryInfoItems sang format WasteHandoverPlanModel để hiển thị trong table
          const mappedDataTable: WasteHandoverPlanModel[] = (detail.deliveryInfoItems || []).map((item: DeliveryInfoItem) => ({
            stt: item.stt || 0,
            wasteHandoverPlanId: item.wasteHandoverPlanId,
            purchaseOrderDetailId: item.wasteHandoverPlanId,
            deliveryDate: detail.deliveryTimeItems?.[0]?.deliveryTime || '',
            wasteHandoverPlanCode: item.wasteHandoverPlanCode,
            productCode: item.productCode,
            materialName: item.productName,
            productName: item.productName,
            vendorNumber: detail.vendorNumber,
            vendorName: detail.vendorName,
            storageLocation: item.storageLocation,
            storageLocationName: item.storageLocationName,
            handoverDocumentNumber: '',
            unit: item.unit || 'KG',
            deliveredQuantity: item.deliveredQuantity || 0,
            actualHandoverQuantity: item.deliveredQuantity || 0,
            saleOrgCode: '',
            actived: true,
            isChoCanRa: (item as any).isChoCanRa,
            createTime: '',
            wasteGroupingContractName: item.wasteGroupingContractName,
            firstWeight: item.firstWeight,
            secondWeight: item.secondWeight,

            packingWeight: item.packingWeight,

            actualDeliveryWeight: item.actualDeliveryWeight,
            deliveryStatus: item.deliveryStatus,
            deliveryStatusName: item.deliveryStatusName,
            note: item.note,
            // Lưu wasteHandoverRegistrationDetailId để truyền lên khi update
            wasteHandoverRegistrationDetailId: (item as any).wasteHandoverRegistrationDetailId,
          } as unknown as WasteHandoverPlanModel));
          setDataTable(mappedDataTable)

          // Set form values - dùng key giống rowKey function: ưu tiên wasteHandoverRegistrationDetailId, sau đó wasteHandoverPlanId
          const dataFormEdit = (detail.deliveryInfoItems || []).reduce((acc: Record<string, any>, cur: DeliveryInfoItem) => {
            const formKey = (cur as any).wasteHandoverRegistrationDetailId || cur.wasteHandoverPlanId;
            acc[formKey] = {
              ...cur,
              actualHandoverQuantity: cur.deliveredQuantity || 0,
              stt: cur.stt,
              firstWeight: cur.firstWeight,
              secondWeight: cur.secondWeight,
              actualDeliveryWeight: cur.actualDeliveryWeight,
              deliveryStatus: cur.deliveryStatus || '',
              note: cur.note || '',
              // Lưu wasteHandoverRegistrationDetailId vào form
              wasteHandoverRegistrationDetailId: (cur as any).wasteHandoverRegistrationDetailId,
              packingWeight: cur.packingWeight ?? null,
            };
            return acc;
          }, {} as Record<string, any>);
          editTableForm.setFieldsValue(dataFormEdit)

          // Set form create values
          const deliveryTimeItems = (detail.deliveryTimeItems || []).map((item: any) => ({
            deliveryTime: item.deliveryTime,
            day: dayjs(item.deliveryTime),
            time: dayjs(item.deliveryTime),
            periodTime: detail.periodTime || 'SANG',
            wasteHandoverRegistrationTimeId: item.wasteHandoverRegistrationTimeId,
            deliveryQuantity: item.deliveryQuantity || 0,
            isDelivered: item.isDelivered || false,
          }));

          formCreate.setFieldsValue({
            vendorNumber: detail.vendorNumber,
            vendorName: detail.vendorName,
            taskStatusName: detail.taskStatusName,
            deliveryTimeItems: deliveryTimeItems,
            isSendEmail: false,
            driverName: detail.driverName,
            phone: detail.phone,
            licensePlate: detail.licensePlate,
            licenseNumber: detail.licenseNumber,
            result: detail.result || 'DAT',
            companionInfo: (detail as any).companionInfo || '',
          })

          setIsCheckedPolicyConfirm(detail.isTickCheckBox || false)
          setPageTitle(`${t('waste-handover-plan-register.update-title')} - ${detail.wasteHandoverRegistrationCode || ''}`)
          setAccessUpdated(true)
        } else {
          message.error(t('waste-handover-plan-register.update-error'));
          navigate(ROUTES.WASTE_HANDOVER_REGISTRATION.LIST_REGISTRATION.LINK);
        }
      } catch (error) {
        setLoading(false)
        message.error(t('waste-handover-plan-register.update-error'));
      }
    }

    useEffect(() => {
      if (wasteHandoverRegistrationId) {
        setLoading(true)
        Promise.allSettled([
          getDetailWasteHandoverRegistration(),
        ]).then(() => {
          setLoading(false)
        })
      } else if (wasteHandoverPlanId) {
        setLoading(true)
        Promise.allSettled([
          getDetailWasteHandoverPlan(),
        ]).then(() => {
          setLoading(false)
        })
      } else if (dataFromList) {
        // Set data từ danh sách và reset STT tăng dần từ 1
        const dataWithResetStt = dataFromList.map((item, index) => {
          // Xử lý productName - có thể là string hoặc object
          let materialName = item.materialName;
          if (!materialName && item.productName) {
            materialName = typeof item.productName === 'string'
              ? item.productName
              : (item.productName as any)?.value || (item.productName as any)?.label || '';
          }

          return {
            ...item,
            stt: index + 1,
            materialName: materialName || item.materialName || '',
            productName: materialName || item.productName || '',
          };
        });
        setDataTable(dataWithResetStt)
        setDataForCreate(dataWithResetStt)
        const dataFormEdit = dataWithResetStt.reduce((acc, cur) => {
          acc[cur.wasteHandoverPlanId] = {
            ...cur,
            deliveryDate: cur.deliveryDate ? dayjs(cur.deliveryDate) : null,
          };
          return acc;
        }, {} as Record<string, any>);
        editTableForm.setFieldsValue(dataFormEdit)

        // Set form create với giá trị vendor từ item đầu tiên
        // và thời gian bàn giao dự kiến = giờ sớm nhất trong các kế hoạch được chọn
        if (dataFromList.length > 0) {
          const firstItem = dataFromList[0];

          // Tìm deliveryDate sớm nhất trong danh sách (bỏ qua item không có deliveryDate)
          const earliestDelivery = dataFromList
            .map(item => item.deliveryDate)
            .filter((d): d is string => !!d)
            .reduce<string | undefined>((min, cur) => {
              if (!min) return cur;
              return dayjs(cur).isBefore(dayjs(min)) ? cur : min;
            }, undefined) || dayjs().toISOString();

          const earliest = dayjs(earliestDelivery);

          formCreate.setFieldsValue({
            vendorName: firstItem.vendorName,
            vendorNumber: firstItem.vendorNumber,
            deliveryTimeItems: [{
              deliveryTime: earliest.toISOString(),
              day: earliest,
              time: earliest,
              periodTime: 'SANG',
            }]
          })
        }
      }
      Promise.allSettled([
        loadWasteOptions(),
        loadVendorOptions(),
        loadStorageOptions(),
        loadWasteGroupOptions(),
        loadContractOptions(),
      ])
    }, []);

    const isCancelledRegistration = (() => {
      if (!wasteHandoverRegistrationId || !dataDetailRegistration) return false;
      // Phiếu hủy: BE set Actived=false, trạng thái task vẫn có thể là VT
      if (dataDetailRegistration.actived === false) return true;
      const statusCode = (dataDetailRegistration.taskStatusCode || '').toUpperCase();
      const statusName = (dataDetailRegistration.taskStatusName || '').toUpperCase();
      return statusCode === 'HUY'
        || statusCode === 'CANCEL'
        || statusCode === 'CANCELLED'
        || statusName.includes('HỦY')
        || statusName.includes('HUY');
    })();

    // Kiểm tra trạng thái phiếu có cho phép sửa không
    const canEditByStatus = () => {
      if (!wasteHandoverRegistrationId || !dataDetailRegistration) return true;
      if (isCancelledRegistration) return false;
      const statusCode = dataDetailRegistration.taskStatusCode;
      // Các trạng thái cho phép sửa: Vừa tạo, Từ chối vào cổng, Chờ bàn giao, VC
      const allowedStatuses = ['VT', 'TCVC', 'CVC'];
      const canEdit = !statusCode || allowedStatuses.includes(statusCode);
      return canEdit;
    }

    // Kiểm tra trạng thái phiếu có cho phép sửa không
    const showEditBtnByStatus = () => {
      if (!wasteHandoverRegistrationId || !dataDetailRegistration) return true;
      if (isCancelledRegistration) return false;
      // Kiểm tra role: chỉ cho phép BGA_CT, BGA, NCC
      const isAllowedRole = roles.includes('BGA_CT') || roles.includes('BGA') || roles.includes('NCC_CT') || roles.includes('XNKH');
      if (!isAllowedRole) return false;

      const statusCode = dataDetailRegistration.taskStatusCode;
      // Các trạng thái cho phép sửa: Vừa tạo, Từ chối vào cổng, Chờ bàn giao, VC
      const allowedStatuses = ['VT', 'TCVC', 'CVC', 'VC', 'XACNHAN', 'CANVAO', 'CBG', 'CCR'];
      const canEdit = !statusCode || allowedStatuses.includes(statusCode);
      return canEdit;
    }

    // Kiểm tra xem checkbox gửi mail có được tích không (chỉ khi trạng thái là VT, TCVAOCONG, CVC và role là BGA_CT)
    const canCheckEmailCheckbox = () => {
      if (!wasteHandoverRegistrationId || !dataDetailRegistration) return true; // Nếu không phải edit registration thì cho phép
      if (isCancelledRegistration) return false;
      const statusCode = dataDetailRegistration.taskStatusCode;
      const allowedStatuses = ['VT', 'TCVC', 'CVC'];
      const isAllowedStatus = !statusCode || allowedStatuses.includes(statusCode);
      const isBGA_CT = roles.includes('BGA_CT');
      return isAllowedStatus && isBGA_CT;
    }

    // Kiểm tra quyền sửa thông tin tài xế
    const canEditDriverInfo = () => {
      if (!wasteHandoverRegistrationId) return true; // Nếu không phải edit registration thì cho phép
      if (!canEditByStatus()) return false;
      // BGA_CT, NCC_CT, BGA đều được sửa thông tin tài xế
      return roles.includes('BGA_CT') || roles.includes('NCC_CT') || roles.includes('BGA');
    }

    // Kiểm tra quyền sửa thời gian giao hàng và email
    const canEditDeliveryTimeAndEmail = () => {
      if (!wasteHandoverRegistrationId) return true; // Nếu không phải edit registration thì cho phép
      if (!canEditByStatus()) return false;
      // Chỉ BGA_CT được sửa thời gian giao hàng và email
      return roles.includes('BGA_CT');
    }

    const disabledField = () => {
      if (!wasteHandoverPlanId && !wasteHandoverRegistrationId) return false;
      if (wasteHandoverRegistrationId) {
        if (isCancelledRegistration) return true;
        // Nếu là edit registration, kiểm tra theo role và trạng thái
        const canEdit = showEditBtnByStatus();
        return !canEdit;
      }
      return !accessUpdated;
    }

    return (
      <PageWrapper breadcrumb={[{
        text: pageTitle
      }
      ]}
        extras={
          <>
          <ActionButtons
            wasteHandoverPlanServices={wasteHandoverPlanServices}
            wasteHandoverRegistrationId={wasteHandoverRegistrationId}
            wasteHandoverPlanId={wasteHandoverPlanId}
            wasteHandoverRegistrationCode={dataDetailRegistration?.wasteHandoverRegistrationCode}
            deliveryInfoItems={dataDetailRegistration?.deliveryInfoItems || []}
            loading={loading}
            disabledField={disabledField}
            hideSaveButtons={isCancelledRegistration}
            onSave={() => {
              formCreate.validateFields().then(values => {
                handleSave(values);
              }).catch(err => { });
            }}
            onUpdate={async (option?: {
              isHideMessage?: boolean;
              isContinue?: boolean;
              tareData?: {
                noPacking: number;
                perPackingWeight: number;
                packingWeight: number;
              };
              confirmedDetailIds?: string[];
              deliveryStatusesMap?: Record<string, string>;
              outboundWeighingData?: {
                secondWeight: number;
                deliveredQuantity: number;
              };
            }) => {
              return formCreate.validateFields().then(async values => {
                if (wasteHandoverPlanId) {
                  return await handleUpdate(values, option);
                } else if (wasteHandoverRegistrationId) {
                  return await handleUpdate(values, option);
                }
                return false;
              }).catch(err => {
                return Promise.resolve(false);
              });
            }}
            onReload={async () => {
              if (wasteHandoverRegistrationId) {
                // Reload detail registration data
                await getDetailWasteHandoverRegistration();
                // Reload status timeline
                transitionStatusRef.current?.reload();
                // Reload timeline
                timelineRef.current?.reload();
              }
            }}
          />
          {wasteHandoverRegistrationId && canCancel && dataDetailRegistration?.isShowCancelButton && (
            <CustomButton
              color="#cd201f"
              type="primary"
              icon={<Trash className="!fill-white" width="14" height="14" />}
              loading={cancelLoading}
              onClick={() => setModalCancelOpen(true)}
            >
              Hủy phiếu
            </CustomButton>
          )}
          </>
        }
      >
        <BoxContainer className="!bg-transparent !p-0">
          <Tabs
            tabBarStyle={{ display: (!wasteHandoverPlanId && !wasteHandoverRegistrationId) ? "none" : 'unset' }}
            items={[
              {
                key: '1',
                label: <strong>Thông tin chung</strong>,
                children: (
                  <Spin spinning={loading}>
                    <div className="flex flex-col gap-default">
                      {/* Trạng thái phiếu */}
                      {wasteHandoverRegistrationId && (
                        <WasteHandoverRegistrationTransitionStatus
                          ref={transitionStatusRef}
                          wasteHandoverPlanServices={wasteHandoverPlanServices}
                          wasteHandoverRegistrationId={wasteHandoverRegistrationId}
                        />
                      )}
                      <SearchForm
                        column="col-1"
                        form={formCreate}
                        disabled={isCancelledRegistration}
                      >
                        <div className="lg:grid lg:grid-cols-2 gap-default">
                          {/* ================= THÔNG TIN CHUNG ======================= */}
                          <GeneralInformation
                            vendorNumber={String(dataFromList?.[0]?.vendorNumber || dataDetail?.vendorNumber || dataDetailRegistration?.vendorNumber || '')}
                            accessUpdated={accessUpdated}
                            disabledField={() => isCancelledRegistration || (wasteHandoverRegistrationId ? !canEditDeliveryTimeAndEmail() : disabledField())}
                            formCreate={formCreate}
                            role={role}
                            dataFromList={dataFromList}
                            wasteHandoverPlanId={wasteHandoverPlanId}
                            wasteHandoverRegistrationId={wasteHandoverRegistrationId}
                            canCheckEmailCheckbox={wasteHandoverRegistrationId ? canCheckEmailCheckbox : () => true}
                          />
                          {/* ======================= THÔNG TIN TÀI XẾ ======================================================= */}
                          <DriverInformation
                            disabledField={() => isCancelledRegistration || (wasteHandoverRegistrationId ? !canEditDriverInfo() : disabledField())}
                            formCreate={formCreate}
                            isCheckedPolicyConfirm={isCheckedPolicyConfirm}
                            onPolicyConfirmChange={handlePolicyConfirmChange}
                          />
                        </div>
                      </SearchForm>
                      {/* === THÔNG TIN BÀN GIAO =============== */}
                      {isEditMode ? (
                        <HandoverInfoEdit
                          editTableForm={editTableForm}
                          editableFormRef={editableFormRef}
                          dataSource={dataTable}
                          wasteOptions={wasteOptions}
                          contractOptionsMap={contractOptionsMap}
                          storageOptions={storageOptions}
                          setDataTable={setDataTable}
                          isThemSuaChatThai={dataDetailRegistration?.isThemSuaChatThai || false}
                          disabledAll={isCancelledRegistration}
                        />
                      ) : (
                        <HandoverInfoCreate
                          editTableForm={editTableForm}
                          editableFormRef={editableFormRef}
                          dataSource={dataForCreate.length > 0 ? dataForCreate : (dataFromList || [])}
                          wasteOptions={wasteOptions}
                          storageOptions={storageOptions}
                          dataForCreate={dataForCreate}
                          dataFromList={dataFromList}
                          setDataForCreate={setDataForCreate}
                          disabledField={disabledField}
                        />
                      )}
                      {/* TIMELINE */}
                      {wasteHandoverRegistrationId && (
                        <WasteHandoverRegistrationTimeline
                          ref={timelineRef}
                          wasteHandoverPlanServices={wasteHandoverPlanServices}
                          wasteHandoverRegistrationId={wasteHandoverRegistrationId}
                        />
                      )}
                    </div>
                  </Spin>
                )
              },
              // Lịch sử sửa
              {
                key: '2',
                destroyInactiveTabPane: true,
                label: <strong>Lịch sử sửa</strong>,
                children: (
                  <CardBox title="Lịch sử chỉnh sửa">
                    <TabHistory />
                  </CardBox>
                )
              }
            ]} />
        </BoxContainer>
        {wasteHandoverRegistrationId && (
          <CancelConfirm
            open={modalCancelOpen}
            onOpenChange={(open) => {
              setModalCancelOpen(open);
            }}
            loading={cancelLoading}
            dataCancel={{
              wasteHandoverRegistrationId: wasteHandoverRegistrationId,
              wasteHandoverRegistrationCode: dataDetailRegistration?.wasteHandoverRegistrationCode || '',
            }}
            onFormSubmit={(value) => {
              value.then(async (data) => {
                setCancelLoading(true);
                const result = await wasteHandoverPlanPresenter.cancelWasteHandoverRegistration(data);
                setCancelLoading(false);
                if (result) {
                  setModalCancelOpen(false);
                  navigate(ROUTES.WASTE_HANDOVER_REGISTRATION.LIST_REGISTRATION.LINK);
                }
              }).catch(() => {
                setCancelLoading(false);
              });
            }}
          />
        )}
      </PageWrapper>
    )
  })
}

